<template>
    <page-container>
        <page-header>
            {{ props.templateView ? 'Snippet Templates' : 'Snippets' }}

            <template v-slot:actions>
                <vx-button v-if="!props.withoutOverview" @click="showOverview = !showOverview" data-test="toggle-overview" type="button"
                           color="muted-dark" size="lg">
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon widthAuto class="hidden! sm:inline-block!"
                                       :icon="faCircleQuestion"></font-awesome-icon>
                    <font-awesome-icon widthAuto class="inline-block! sm:hidden!"
                                       :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <snippets-overview v-if="!props.withoutOverview" v-show="showOverview" class="mb-14" :templateView="props.templateView"></snippets-overview>

        <snippet-list-controls v-if="list.length || !isEqual(defaultFilters(), fetchParams)"
            v-model="fetchParams"
           @new="snippetModalShow = true"
           @apply="applyFilters"
           @reset="resetFilters"
           :loading="loading"
           :readOnly="props.readOnly"
           :templateView="props.templateView"
           ></snippet-list-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else>
                No snippets yet.
                <router-button v-if="props.templateView" data-test="empty-add-button" :to="{ name: 'snippets.templates.new' }">
                    New Template <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </router-button>
                <vx-button v-else-if="!props.readOnly" data-test="snippet-modal-list" @click="snippetModalShow = true" type="button">
                    New Snippet <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </empty-state>
        </template>

        <div v-if="list.length">
            <vx-alert v-if="props.groupId && lockedSnippets" color="info" :closeable="false" shadow class="my-4">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faDiamondExclamation" widthAuto></font-awesome-icon>
                </template>
                Quick Blast is limited to Locked Snippets by Corporate
            </vx-alert>

            <div
                class="grid lg:grid-cols-2 grid-cols-1 gap-6"
                :class="props.groupId && lockedSnippets ? 'my-8' : 'my-12'"
            >
                <snippet-list-card v-for="(item, index) in list"
                    :key="item.id"
                    :modelValue="list[index]"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    :readOnly="props.readOnly"
                    :templateView="props.templateView"
                    @edit="edit"
                    @deleted="removeFromList($event)"/>
            </div>
        </div>

        <div v-if="list.length" class="flex justify-between items-center w-full gap-10">
            <vx-button v-if="fetchParams.cursor"
                @click="loadData()"
                data-test="load-more-btn"
                color="muted-dark"
                size="lg"
                class="grow-0"
                :loading="loading">
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>

            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                {{ list.length }} / {{ totalCount }}
            </div>
        </div>

        <modal-snippet v-if="!props.templateView"
            :groupId="props.groupId"
            :orgUnitId="props.orgUnitId"
            v-model:visible="snippetModalShow"
            v-model="selectedSnippet"
            @saved="resourceCreatedOrUpdated($event)"
        />
    </page-container>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCircleQuestion,
    faSpinner,
    faCirclePlus,
    faDiamondExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import PageContainer from '~/components/general/PageContainer.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import ModalSnippet from '../snippets/components/ModalSnippet.vue';
import SnippetsOverview from './components/SnippetsOverview.vue';
import SnippetListControls from './components/SnippetListControls.vue';
import { snippetsClient } from '~/services';
import { isEqual } from '~/utils/helpers';
import dayjs from '~/utils/dayjs';
import { useCursorPagination } from '~/composables/useCursorPagination';
import SnippetListCard from '~/components/snippets/components/SnippetListCard.vue';
import { useToasted } from '~/composables/useToasted';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';

const props = defineProps({
    groupId: String,
    orgUnitId: Number,
    readOnly: {
        type: Boolean,
        default: false,
    },
    withoutOverview: {
        type: Boolean,
        default: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();
const showOverview = ref(true);
const totalCount = ref(0);
const toasted = useToasted();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const { getMessageRules, lockedSnippetsEnabled } = useMessageRules();

const defaultFilters = () => {
    const defaultFilters = {
        query: '',
        cursor: null,
        sort_field: 'id',
        sort_dir: 'desc',
        per_page: 10,
    }

    if (!props.orgUnitId && !props.groupId) {
        defaultFilters['filter[is_template]'] = props.templateView ? 1 : 0;
    }

    if (!onCorporate() || props.templateView) {
        defaultFilters['filter[is_locked]'] = undefined;
    }

    return defaultFilters;
};

const selectedSnippet = ref({
    id: null,
    title: '',
    description: '',
    body: '',
    media_url: '',
    media_content_type: '',
});

const snippetModalShow = ref(false);
const lockedSnippets = computed(() => lockedSnippetsEnabled('marketing'));

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: undefined,
    }

    loadData();
}

const loadData = () => {
    fetchRecords(
        snippetsClient.getSnippets(
            { teamId, orgUnitId: props.orgUnitId, groupId: props.groupId },
            { ...fetchParams.value }
        )
    );

    fetchTotalCount();
}

const fetchTotalCount = async () => {
    try {
        const response = await snippetsClient.getSnippetsCount(
            { teamId, orgUnitId: props.orgUnitId, groupId: props.groupId },
            { ...fetchParams.value }
        );

        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const removeFromList = (id) => {
    list.value = list.value.filter((item) => item.id !== id);

    if (!list.value.length) {
        loadData();
    }
};

const resourceCreatedOrUpdated = (data) => {
    if (data.created_at && dayjs.utc(data.created_at).isAfter(dayjs().subtract(5, 'seconds'))) {
        list.value = [
            { ...data },
            ...list.value
        ];

        fetchTotalCount();
    } else {
        list.value = list.value.map(snippet => {

            if (snippet.id === data.id) {
                return {
                    ...snippet,
                    ...data,
                };
            }

            return snippet;
        })
    }
};

const edit = (snippet) => {
    selectedSnippet.value = {...snippet};
    snippetModalShow.value = true;
};

watch(
    () => [props.templateView, props.groupId, props.orgUnitId],
    () => resetFilters()
);

onMounted(() => {
    resetFilters();

    if (props.groupId) {
        getMessageRules(props.groupId);
    }
})
</script>
